import { Statuses } from '@eagle/common';
import { DomainPublicConfigResponse } from '@eagle/core-data-types';
import { CssBaseline, ThemeProvider as UiToolkitThemeProvider, useThemeSwitch } from '@ethings/ui-toolkit';
import { Theme, ThemeOptions, ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { SnackbarProvider } from 'notistack';
import { createContext, FC, lazy, PropsWithChildren, StrictMode, Suspense, useContext, useEffect, useState } from 'react';
import { createRoutesFromChildren, matchRoutes, HashRouter as Router, useLocation, useNavigationType } from 'react-router-dom';
import { ProvideAuth } from '../../auth';
import { ConfigProvider, TitleProvider, useTitle, useUnregisterServiceWorker } from '../../hooks';
import { DomainI18nProvider } from '../../providers';
import { createThemeOptions, theme } from '../../theme';
import { Undefinable } from '../../types';
import { DEFAULT_CONFIG } from '../../util/config';
import { LD_DEFAULT_CONTEXT } from '../flags';
import { MiddleSpinner } from '../middle-spinner';
import { Snackbar } from '../snackbar';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false },
  },
});

const Loading: FC = () => {
  useTitle('⌛ ...');
  return <MiddleSpinner />;
};

const MATCH_FEATURE_FLAG = /\/featureflag\//;

interface ThemeContextMap {
  setDomainTheme: (domain: string) => void;
  domainTheme: string;
}

export const domainThemeContext = createContext<Undefinable<ThemeContextMap>>(undefined);

interface DomainThemeProviderProps extends PropsWithChildren {
  themeOptions?: ThemeOptions;
}

export const DomainThemeProvider: FC<DomainThemeProviderProps> = ({ children, themeOptions }) => {
  const [createdTheme, setCreatedTheme] = useState<Theme>(theme());
  const [domainTheme, setDomainTheme] = useState<string>('');
  const { setTheme } = useThemeSwitch();

  const snackbarComponents = {
    success: Snackbar,
    error: Snackbar,
    info: Snackbar,
  };

  useEffect(() => {
    if (themeOptions) {
      const toolkitThemeOptions = createThemeOptions(themeOptions);
      setTheme(toolkitThemeOptions);
      setCreatedTheme(theme(themeOptions));
      return;
    }

    const getThemeJson = async (): Promise<ThemeOptions | undefined> => {
      const response = await axios.get<ThemeOptions>(`/mui/theme.json?domain=${domainTheme}`, { validateStatus: () => true });
      if (response.status === Statuses.OK) {
        return response.data;
      }
    };

    getThemeJson().then((themeData) => {
      if (themeData) {
        const toolkitThemeOptions = createThemeOptions(themeData);
        setTheme(toolkitThemeOptions);
        setCreatedTheme(theme(themeData));
      }
    }).catch(() => {
      setCreatedTheme(theme());
    });
  }, [domainTheme, setTheme, themeOptions]);

  if (!createdTheme) {
    return <></>;
  }

  return <domainThemeContext.Provider
    value={{
      setDomainTheme,
      domainTheme,
    }}
  ><ThemeProvider theme={createdTheme}>
      <SnackbarProvider maxSnack={3} Components={snackbarComponents}>
        {children}
      </SnackbarProvider>
    </ThemeProvider>
  </domainThemeContext.Provider>;
};

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export const useDomainTheme = function (): ThemeContextMap {
  const data = useContext(domainThemeContext);
  if (!data) throw new Error('Missing DomainThemeProvider in tree above useDomainThemeContext');
  return data;
};

const DomainConfigProvider = lazy(async () => {
  const response = await axios.get<DomainPublicConfigResponse>('/api/public-config', { validateStatus: () => true });
  const config = response.status === Statuses.OK ? response.data : DEFAULT_CONFIG;
  const commitSHA = import.meta.env.VITE_COMMIT_SHA || import.meta.env.REACT_APP_COMMIT_SHA; // TODO: DEV-371

  if (config.sentry?.dsn && commitSHA && config.environment !== 'test') {
    const dsn = config.sentry.dsn
      .replace('https://', `${location.protocol}//`)
      .replace(/[a-z0-9]+\.ingest\.sentry\.io\//, `${location.host}/performance/`);
    Sentry.init({
      dsn,
      environment: config.environment,
      release: `ethings-portals-${commitSHA}`,

      integrations: [
        new BrowserTracing({
          routingInstrumentation: Sentry.reactRouterV6Instrumentation(
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          ),
          shouldCreateSpanForRequest: (url) => {
            // Do not create spans for outgoing requests to a `/featureflag/` endpoint
            return !url.match(MATCH_FEATURE_FLAG);
          },
        }),
        new Sentry.Replay({
          maskAllText: false,
          maskAllInputs: false,
        }),
      ],

      denyUrls: [MATCH_FEATURE_FLAG],

      beforeBreadcrumb: (breadcrumb, hint) => {
        const xhr = hint?.xhr as XMLHttpRequest | undefined;
        return breadcrumb.category === 'xhr' && xhr?.responseURL.match(MATCH_FEATURE_FLAG) ? null : breadcrumb;
      },

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: config?.sentry?.tracesSampleRate ?? 0.2,

      // This sets the sample rate to be 10%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: config?.sentry?.replaysSessionSampleRate ?? 0.1,

      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: config?.sentry?.replaysOnErrorSampleRate ?? 0.5,
    });
  }

  if (!config.launchDarkly) {
    const Provider: FC<PropsWithChildren> = ({ children }) => <ConfigProvider config={config}>{children}</ConfigProvider>;
    return { default: Provider };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const noop = () => { }; // No Operation function, does nothing

  const silentLogger = {
    debug: noop,
    error: noop,
    info: noop,
    warn: noop,
  };

  // cSpell: ignore featureflag
  const FlagProvider = await asyncWithLDProvider({
    clientSideID: config.launchDarkly.clientSideId,
    context: LD_DEFAULT_CONTEXT,
    options: {
      baseUrl: '/featureflag',
      bootstrap: 'localStorage',
      eventsUrl: '/featureflag',
      logger: silentLogger,
      streamUrl: '/featureflag',
    },
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
  });

  const Provider: FC<PropsWithChildren> = ({ children }) => (
    <ConfigProvider config={config}>
      <Suspense fallback={<Loading />}>
        {config.launchDarkly ? (
          <FlagProvider>{children}</FlagProvider>
        ) : (
          <>{children}</>
        )}
      </Suspense>
    </ConfigProvider>
  );

  return { default: Provider };
});

export const AppWrapper: FC<PropsWithChildren> = ({ children }) => {
  useUnregisterServiceWorker();

  return (
    <StrictMode>
      <TitleProvider>
        <Suspense fallback={<Loading />}>
          <DomainConfigProvider>
            <UiToolkitThemeProvider>
              <DomainThemeProvider>
                <QueryClientProvider client={queryClient}>
                  <CssBaseline />
                  <ProvideAuth>
                    <DomainI18nProvider>
                      <Router>
                        {children}
                      </Router>
                    </DomainI18nProvider>
                  </ProvideAuth>
                </QueryClientProvider>
              </DomainThemeProvider>
            </UiToolkitThemeProvider>
          </DomainConfigProvider>
        </Suspense>
      </TitleProvider>
    </StrictMode>
  );
};
